import * as React from 'react';

import { t } from '~/i18n';
import { formatDateTime } from '@ixicare/ui';
import { observer } from 'mobx-react';
import Alert from '~store/Alert/Alert';
import { HcpGroupHistory } from '@ixicare/resources';
import { useStores } from '~store/utils';
import { useHcpGroups } from '~hooks/useHcpGroups';

interface AlertSegmentationLogsProps {
  alert: Alert;
}
const AlertSegmentationLogs: React.FC<AlertSegmentationLogsProps> = observer(({ alert }) => {
  const { alerts } = useStores();

  React.useEffect(() => {
    alerts.fetchAlert(alert?.uuid);
  }, [alert?.uuid]);

  return (
    <ul className="list">
      {alert.hcpGroupHistory
        ?.slice()
        .sort((a, b) => {
          return a.counter - b.counter;
        })
        .map((historyEntry, index) => (
          <HcpGroupHistoryItem
            key={`${historyEntry.counter}-${index}`}
            historyEntry={historyEntry}
            siteUuid={alert.siteUuid ?? undefined}
          />
        ))}
    </ul>
  );
});

export function HcpGroupHistoryItem({ historyEntry, siteUuid }: { historyEntry: HcpGroupHistory; siteUuid?: string }) {
  const { data: hcpGroups } = useHcpGroups(siteUuid, { staleTime: 5 * 60 * 1000 });

  const getGroupName = (id: string | null) => {
    if (!id) return null;
    return hcpGroups?.find((g) => g.id === id)?.hcpGroupName ?? id;
  };

  const groupName = getGroupName(historyEntry.hcpGroupId);

  if (historyEntry.noSegmentation) {
    return (
      <li>
        {t('alert.segmentation.logs.no_segmentation', {
          datetime: formatDateTime(historyEntry.createdAt),
          reason: historyEntry.reason || '',
        })}
      </li>
    );
  }

  const translationKey = historyEntry.reason
    ? 'alert.segmentation.logs.group_with_reason'
    : 'alert.segmentation.logs.group';

  return (
    <li>
      {t(translationKey, {
        datetime: formatDateTime(historyEntry.createdAt),
        groupName: groupName || '-',
        reason: historyEntry.reason || '',
      })}
    </li>
  );
}

export default AlertSegmentationLogs;
